import { BACKUP_FORMAT, BACKUP_VERSION } from "./exports";
import { rebuildSections } from "./parser";
import { normalizeScriptDocument } from "./script-normalizer";
import type { AppSettings, Character, EntryKind, ParserMode, ScriptDocument, ScriptEntry, ScriptPage } from "./types";

const ENTRY_KINDS = new Set<EntryKind>(["dialogue", "stageDirection", "heading", "note", "pageMarker"]);
const PARSER_MODES = new Set<ParserMode>(["auto", "left", "top", "tag", "none", "fountain", "fdx"]);

export function restoreScriptBackup(source: string, defaults: AppSettings): ScriptDocument {
  let parsed: unknown;
  try {
    parsed = JSON.parse(source);
  } catch {
    throw new Error("This backup is not valid JSON.");
  }
  const root = requireRecord(parsed, "The backup does not contain a script document.");
  const record = readBackupDocument(root);
  const pages = requireArray(record.pages, "The backup has no script pages.").map(readPage);
  const characters = requireArray(record.characters, "The backup has no character list.").map(readCharacter);
  const now = new Date().toISOString();
  const document: ScriptDocument = {
    id: requireString(record.id, "The backup has no script ID."),
    title: requireString(record.title, "The backup has no title."),
    sourceText: requireString(record.sourceText, "The backup has no original source text."),
    importMode: record.importMode === "file" ? "file" : "text",
    parserMode: PARSER_MODES.has(record.parserMode as ParserMode) ? record.parserMode as ParserMode : "auto",
    pages,
    characters,
    sections: [],
    settings: readSettings(record.settings, defaults),
    memorisation: readMemorisation(record.memorisation),
    tags: readStringArray(record.tags),
    notes: typeof record.notes === "string" ? record.notes : "",
    folder: optionalString(record.folder)?.trim() || undefined,
    createdAt: validDate(record.createdAt) ?? now,
    updatedAt: now,
  };
  normalizeScriptDocument(document, defaults);
  rebuildSections(document);
  return document;
}

function readBackupDocument(root: Record<string, unknown>): Record<string, unknown> {
  const isStructuredBackup = "format" in root || "version" in root || "script" in root;
  if (!isStructuredBackup) return root;
  if (root.format !== BACKUP_FORMAT) throw new Error("This is not a LineByLine script backup.");
  if (root.version !== BACKUP_VERSION) throw new Error(`Backup version ${String(root.version)} is not supported. This app supports version ${BACKUP_VERSION}.`);
  if (!validDate(root.exportedAt)) throw new Error("The backup has no valid export date.");
  return requireRecord(root.script, "The backup has no script data.");
}

function readPage(value: unknown, index: number): ScriptPage {
  const page = requireRecord(value, `Page ${index + 1} is invalid.`);
  return {
    id: requireString(page.id, `Page ${index + 1} has no ID.`),
    pageNumber: typeof page.pageNumber === "number" ? page.pageNumber : undefined,
    entries: requireArray(page.entries, `Page ${index + 1} has no entries.`).map(readEntry),
  };
}

function readEntry(value: unknown, index: number): ScriptEntry {
  const entry = requireRecord(value, `Entry ${index + 1} is invalid.`);
  const text = requireString(entry.text, `Entry ${index + 1} has no text.`);
  const kind = ENTRY_KINDS.has(entry.kind as EntryKind) ? entry.kind as EntryKind : "note";
  return {
    id: requireString(entry.id, `Entry ${index + 1} has no ID.`),
    kind,
    speakerId: optionalString(entry.speakerId),
    speakerLabel: optionalString(entry.speakerLabel),
    text,
    rawText: typeof entry.rawText === "string" ? entry.rawText : text,
    pageNumber: typeof entry.pageNumber === "number" ? entry.pageNumber : undefined,
  };
}

function readCharacter(value: unknown, index: number): Character {
  const character = requireRecord(value, `Character ${index + 1} is invalid.`);
  return {
    id: requireString(character.id, `Character ${index + 1} has no ID.`),
    name: requireString(character.name, `Character ${index + 1} has no name.`),
    aliases: readStringArray(character.aliases),
    colour: optionalString(character.colour),
    voiceId: optionalString(character.voiceId),
  };
}

function readSettings(value: unknown, defaults: AppSettings): ScriptDocument["settings"] {
  const settings = typeof value === "object" && value !== null ? value as Record<string, unknown> : {};
  const voice = typeof settings.voiceReading === "object" && settings.voiceReading !== null ? settings.voiceReading as Record<string, unknown> : {};
  const recognition = typeof settings.recognition === "object" && settings.recognition !== null ? settings.recognition as Record<string, unknown> : {};
  const revealModes = new Set(["normal", "initials", "wordByWord", "underline"]);
  const legacyRevealMode = settings.revealMode as string;
  return {
    selectedCharacterIds: readStringArray(settings.selectedCharacterIds),
    revealMode: revealModes.has(legacyRevealMode) ? legacyRevealMode as ScriptDocument["settings"]["revealMode"] : "normal",
    hideMine: typeof settings.hideMine === "boolean" ? settings.hideMine : legacyRevealMode === "hideMine",
    hideOthers: typeof settings.hideOthers === "boolean" ? settings.hideOthers : legacyRevealMode === "hideOthers",
    showOtherLinesNormally: typeof settings.showOtherLinesNormally === "boolean" ? settings.showOtherLinesNormally : false,
    voiceReading: {
      readOthersLines: typeof voice.readOthersLines === "boolean" ? voice.readOthersLines : true,
      readMyLines: typeof voice.readMyLines === "boolean" ? voice.readMyLines : false,
      autoProceed: typeof voice.autoProceed === "boolean" ? voice.autoProceed : false,
      speed: finiteNumber(voice.speed, defaults.defaultPlaybackSpeed),
      pitch: finiteNumber(voice.pitch, 1),
      volume: finiteNumber(voice.volume, 1),
      language: typeof voice.language === "string" ? voice.language : defaults.defaultLanguage,
    },
    recognition: {
      threshold: finiteNumber(recognition.threshold, defaults.recognitionThreshold),
      ignoredWords: readStringArray(recognition.ignoredWords).length ? readStringArray(recognition.ignoredWords) : [...defaults.ignoredWords],
    },
  };
}

function readMemorisation(value: unknown): ScriptDocument["memorisation"] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const result: ScriptDocument["memorisation"] = {};
  for (const [entryId, candidate] of Object.entries(value)) {
    if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) continue;
    const stat = candidate as Record<string, unknown>;
    const dueAt = validDate(stat.dueAt);
    if (!dueAt) continue;
    result[entryId] = {
      attempts: finiteNumber(stat.attempts, 0),
      successes: finiteNumber(stat.successes, 0),
      averageScore: finiteNumber(stat.averageScore, 0),
      intervalDays: finiteNumber(stat.intervalDays, 0),
      dueAt,
      lastAttemptAt: validDate(stat.lastAttemptAt),
      revealCount: finiteNumber(stat.revealCount, 0),
    };
  }
  return result;
}

function requireRecord(value: unknown, message: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(message);
  return value as Record<string, unknown>;
}

function requireArray(value: unknown, message: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(message);
  return value;
}

function requireString(value: unknown, message: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(message);
  return value;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function validDate(value: unknown): string | undefined {
  return typeof value === "string" && Number.isFinite(Date.parse(value)) ? value : undefined;
}
